import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Icon } from "@iconify/react";
import { enqueueSnackbar } from "notistack";
import StaffApiErrorModal, {
  getStaffApiErrorCopy,
} from "../../../../components/order/StaffApiErrorModal";
import { VoidReviewFacts, normalizeVoidOrder } from "./VoidReviewCard";
import getVoidTicketRequest from "../../../../controllers/booking/postTicketing/void/getVoidTicketRequest";
import approveVoidTicket from "../../../../controllers/booking/postTicketing/void/approveVoidTicket";
import denyVoidRequest from "../../../../controllers/booking/postTicketing/void/denyVoidRequest";
import { getVoidReviewGuidance } from "../../../../data/flight/order/getVoidAdminGuidance";
import VoidMessagesModal from "./VoidMessagesModal";

export default function VoidReviewModal({
  open,
  setOpen,
  data,
  onDone,
  onSubmit,
  onManualVoid,
}) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [apiError, setApiError] = useState(null);
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [messagesData, setMessagesData] = useState(null);

  useEffect(() => {
    if (!open) {
      setRejectReason("");
      setOrder(null);
      if (!messagesOpen) {
        setMessagesData(null);
      }
      return;
    }
    const raw = typeof data === "object" ? data : { _id: data };
    setOrder(normalizeVoidOrder(raw));
    if (!raw?._id) return;

    let cancelled = false;
    setLoading(true);
    getVoidTicketRequest(raw._id)
      .then((res) => {
        if (cancelled) return;
        if (res?.return) setOrder(normalizeVoidOrder(res.data));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, data, messagesOpen]);

  function close() {
    if (accepting || declining) return;
    setOpen?.(false);
  }

  async function handleAccept() {
    if (!order?._id) return;
    setAccepting(true);
    const res = await approveVoidTicket(order._id).finally(() =>
      setAccepting(false),
    );
    if (res.return) {
      enqueueSnackbar(
        "Request accepted. Send the void to the airline next.",
        { variant: "success" },
      );
      setOpen?.(false);
      onDone?.();
    } else setApiError(getStaffApiErrorCopy(res));
  }

  async function handleDecline() {
    if (!order?._id) return;
    if (!rejectReason.trim()) {
      enqueueSnackbar("Say why you are declining this void.", {
        variant: "warning",
      });
      return;
    }
    setDeclining(true);
    const res = await denyVoidRequest(order._id, {
      rejectReason: rejectReason.trim(),
    }).finally(() => setDeclining(false));
    if (res.return) {
      enqueueSnackbar("Void request has been declined.", {
        variant: "success",
      });
      setOpen?.(false);
      onDone?.();
    } else setApiError(getStaffApiErrorCopy(res));
  }

  if (!open && !messagesOpen) return null;

  const busy = accepting || declining;
  const review = getVoidReviewGuidance(order || data);

  return (
    <>
      <StaffApiErrorModal
        open={Boolean(apiError)}
        setOpen={(next) => {
          if (!next) setApiError(null);
        }}
        title={apiError?.title}
        text={apiError?.text}
      />
      <VoidMessagesModal
        open={messagesOpen}
        setOpen={(next) => {
          setMessagesOpen(next);
          if (!next && !open) setMessagesData(null);
        }}
        data={messagesData || order || data}
      />
      {open
        ? ReactDOM.createPortal(
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <button
            type="button"
            aria-label="Close overlay"
            className="absolute inset-0 bg-black/40"
            onClick={close}
          />
          <div className="relative w-full max-w-[560px] max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 px-6 pt-5">
              <div>
                <h2 className="text-[17px] font-semibold text-gray-900 leading-6">
                  {review.title}
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  Read why you are here, then check the ticket before you
                  continue. Accepting is not a void yet.
                </p>
              </div>
              <button
                type="button"
                onClick={close}
                className="mt-0.5 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              >
                <Icon icon="material-symbols:close" className="text-xl" />
              </button>
            </div>

            <div className="px-6 pb-6 pt-4">
              {loading && !order?.flightBooking ? (
                <div className="flex items-center justify-center py-10">
                  <div className="primaryLoader" />
                </div>
              ) : (
                <VoidReviewFacts data={order} showQuestions={false} />
              )}

              <p className="mt-4 text-sm text-gray-700">
                We do not notify you when the agent writes here.{" "}
                <button
                  type="button"
                  onClick={() => {
                    if (busy) return;
                    setMessagesData(order || data);
                    setMessagesOpen(true);
                    setOpen?.(false);
                  }}
                  disabled={busy}
                  className="text-[#1D4ED8] underline font-medium hover:text-[#1E40AF] disabled:opacity-60"
                >
                  Check your messages
                </button>
              </p>

              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3">
                <div className="text-sm font-semibold text-amber-900">
                  {review.whyTitle}
                </div>
                {review.statusDisplay ? (
                  <p className="mt-1 text-xs font-medium text-amber-800">
                    Status: {review.statusDisplay}
                  </p>
                ) : null}
                <p className="mt-1 text-sm text-amber-900/90">{review.why}</p>
                {review.note ? (
                  <p className="mt-1 text-xs text-amber-800">
                    System note: {review.note}
                  </p>
                ) : null}
                {review.steps?.length ? (
                  <>
                    <div className="mt-3 text-sm font-semibold text-gray-900">
                      Do this
                    </div>
                    <ol className="mt-1 list-decimal pl-5 space-y-1 text-sm text-gray-800">
                      {review.steps.map((step) => (
                        <li key={step}>{step}</li>
                      ))}
                    </ol>
                  </>
                ) : null}
              </div>

              {review.questions?.length ? (
                <div className="mt-4 text-sm text-gray-700">
                  <div className="font-medium text-gray-900 mb-1">
                    Ask yourself
                  </div>
                  <ol className="list-decimal pl-5 space-y-1">
                    {review.questions.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ol>
                </div>
              ) : null}

              {review.showDecline ? (
                <div className="mt-4">
                  <label className="text-sm font-medium text-gray-800">
                    If you Decline, say why
                  </label>
                  <textarea
                    className="mt-1 w-full min-h-[88px] rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-800 outline-none focus:border-primary1"
                    placeholder="e.g. Void window closed, ticket already used, duplicate request"
                    value={rejectReason}
                    onChange={(ev) => setRejectReason(ev.target.value)}
                    disabled={busy}
                  />
                </div>
              ) : null}

              <div className="mt-4 flex flex-col-reverse sm:flex-row gap-2.5">
                {review.showDecline ? (
                  <button
                    type="button"
                    onClick={handleDecline}
                    disabled={busy}
                    className="h-11 flex-1 rounded-xl bg-red-600 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                  >
                    {declining ? (
                      <span className="buttonLoader mx-auto" />
                    ) : (
                      review.declineLabel
                    )}
                  </button>
                ) : null}
                {review.showManual && onManualVoid ? (
                  <button
                    type="button"
                    onClick={() => {
                      setOpen?.(false);
                      onManualVoid();
                    }}
                    disabled={busy}
                    className="h-11 flex-1 rounded-xl border border-gray-300 bg-white text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-60"
                  >
                    {review.manualLabel}
                  </button>
                ) : null}
                {review.showSubmit && onSubmit ? (
                  <button
                    type="button"
                    onClick={() => {
                      setOpen?.(false);
                      onSubmit();
                    }}
                    disabled={busy}
                    className="h-11 flex-1 rounded-xl bg-primary1 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
                  >
                    {review.submitLabel}
                  </button>
                ) : null}
                {review.showAccept || (review.showSubmit && !onSubmit) ? (
                  <button
                    type="button"
                    onClick={handleAccept}
                    disabled={busy}
                    className="h-11 flex-1 rounded-xl bg-primary1 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
                  >
                    {accepting ? (
                      <span className="buttonLoader mx-auto" />
                    ) : (
                      review.acceptLabel
                    )}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )
        : null}
    </>
  );
}
